import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Session } from '@supabase/supabase-js';
import { ArrowLeft, RefreshCw, ShieldCheck } from 'lucide-react';
import { api, type IntelligenceData } from '../lib/api';
import { Logo } from './Logo';
import './intelligence.css';

const labels: Record<string, string> = {
  topic: 'Tema',
  pillar: 'Pilar',
  series: 'Série',
  format: 'Formato',
  hook: 'Gancho',
  duration: 'Duração',
  hour: 'Horário',
  platform: 'Plataforma',
  data: 'Qualidade dos dados',
};
function percent(value: number | null | undefined): string {
  return value === null || value === undefined
    ? 'Indisponível'
    : (value * 100).toLocaleString('pt-BR', { maximumFractionDigits: 2 }) + '%';
}
export function IntelligenceWorkspace({
  session,
  onBack,
}: {
  session: Session;
  onBack: () => void;
}) {
  const [data, setData] = useState<IntelligenceData | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [codexEnabled, setCodexEnabled] = useState<boolean | null>(null);
  const [codexMessage, setCodexMessage] = useState('');
  const [codexConfirmed, setCodexConfirmed] = useState(false);
  const [codexBusy, setCodexBusy] = useState(false);
  useEffect(() => {
    let active = true;
    void api.intelligence.codexPermission(session).then(
      (permission) => {
        if (active) setCodexEnabled(permission.enabled);
      },
      () => {
        if (active)
          setCodexMessage(
            'Não foi possível consultar o consentimento. Reabra esta tela para tentar novamente.',
          );
      },
    );
    return () => {
      active = false;
    };
  }, [session]);
  async function changeCodexPermission(enabled: boolean) {
    if (codexBusy || codexEnabled === null || (enabled && !codexConfirmed))
      return;
    setCodexBusy(true);
    setCodexMessage('');
    try {
      const permission = await api.intelligence.setCodexPermission(
        session,
        enabled,
      );
      setCodexEnabled(permission.enabled);
      setCodexConfirmed(false);
      setCodexMessage(
        permission.enabled
          ? 'Consentimento editorial registrado com auditoria. Nenhuma execução ou publicação foi iniciada.'
          : 'Consentimento editorial revogado. Novas operações da ponte estão bloqueadas.',
      );
    } catch {
      setCodexEnabled(null);
      setCodexMessage(
        'Não foi possível confirmar o consentimento. Reabra a tela para consultar o estado antes de tentar novamente.',
      );
    } finally {
      setCodexBusy(false);
    }
  }
  const [dimension, setDimension] = useState('all');
  const [platform, setPlatform] = useState('all');
  const [format, setFormat] = useState('all');
  const [period, setPeriod] = useState('all');
  const [subjectQuery, setSubjectQuery] = useState('');
  const [history, setHistory] = useState<{
    publicationId: string;
    rows: Array<{
      hours: number;
      capturedAt: string | null;
      metrics: Record<string, number>;
      rates: Record<string, number>;
    }>;
  } | null>(null);
  const load = useCallback(async () => {
    try {
      setError('');
      setData(await api.intelligence.overview(session));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Falha ao carregar');
    }
  }, [session]);
  useEffect(() => {
    void load();
  }, [load]);
  const summaries = useMemo(
    () =>
      (data?.run?.summary ?? []).filter(
        (row) =>
          (dimension === 'all' || row.dimension === dimension) &&
          (platform === 'all' || row.cohort.startsWith(platform + ':')) &&
          (format === 'all' || row.cohort.split(':')[1] === format) &&
          (period === 'all' || row.cohort.split(':')[3] === period) &&
          row.subject
            .toLocaleLowerCase('pt-BR')
            .includes(subjectQuery.toLocaleLowerCase('pt-BR')),
      ),
    [data, dimension, platform, format, period, subjectQuery],
  );
  const findings = useMemo(
    () =>
      (data?.findings ?? []).filter(
        (row) =>
          (dimension === 'all' || row.dimension === dimension) &&
          (platform === 'all' ||
            String(row.evidence.cohort ?? '').startsWith(platform + ':')) &&
          (format === 'all' ||
            String(row.evidence.cohort ?? '').split(':')[1] === format) &&
          (period === 'all' ||
            String(row.evidence.cohort ?? '').split(':')[3] === period) &&
          row.subject
            .toLocaleLowerCase('pt-BR')
            .includes(subjectQuery.toLocaleLowerCase('pt-BR')),
      ),
    [data, dimension, platform, format, period, subjectQuery],
  );
  const formats = [
    ...new Set(
      (data?.run?.summary ?? [])
        .map((row) => row.cohort.split(':')[1])
        .filter((value): value is string => !!value),
    ),
  ];
  async function analyze() {
    setBusy(true);
    try {
      await api.intelligence.analyze(session);
      await load();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Falha na análise');
    } finally {
      setBusy(false);
    }
  }
  async function decide(id: string, approve: boolean) {
    const reason = approve
      ? 'Aprovado após revisão editorial'
      : 'Rejeitado após revisão editorial';
    setBusy(true);
    try {
      await api.intelligence.decide(session, id, approve, reason);
      await load();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Falha na decisão');
    } finally {
      setBusy(false);
    }
  }
  async function showHistory(publicationId: string) {
    try {
      const result = await api.intelligence.history(session, publicationId);
      setHistory({ publicationId, rows: result.history });
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Falha no histórico');
    }
  }
  async function revert(id: string) {
    setBusy(true);
    try {
      await api.intelligence.revert(session, id);
      await load();
    } catch (cause) {
      setError(
        cause instanceof Error ? cause.message : 'Falha ao reverter ajuste',
      );
    } finally {
      setBusy(false);
    }
  }
  async function updateSettings(
    field:
      'minimum_sample' | 'lookback_days' | 'autonomous_idea_potential_delta',
    value: number,
  ) {
    setBusy(true);
    try {
      await api.intelligence.settings(session, { [field]: value });
      await load();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Falha ao salvar');
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="app-shell intelligence-shell">
      <header>
        <Logo />
        <button className="secondary" onClick={onBack}>
          <ArrowLeft size={17} /> Dashboard
        </button>
      </header>
      <main className="intelligence-main">
        <div className="intelligence-heading">
          <div>
            <p className="eyebrow">MISSÃO 5 · DADOS COM CONTEXTO</p>
            <h1>Otimizador e inteligência</h1>
            <p>
              Comparações explicáveis, memória editorial e recomendações que não
              substituem o DNA.
            </p>
          </div>
          <button
            className="secondary"
            disabled={busy || !data}
            onClick={() => void analyze()}
          >
            <RefreshCw size={16} /> Analisar agora
          </button>
        </div>
        {error && (
          <p className="intelligence-error" role="alert">
            {error}
          </p>
        )}
        <section
          className="intelligence-status"
          aria-labelledby="codex-consent-title"
        >
          <div>
            <h2 id="codex-consent-title">Inteligência editorial do Codex</h2>
            <p>
              Permite somente ler histórico e métricas pelas integrações
              existentes e registrar relatórios, aprendizados rastreáveis e
              pautas para revisão. Não permite publicar, agendar posts, executar
              OAuth, acessar tokens sociais ou despausar automações.
            </p>
            <p>
              Estado:{' '}
              {codexEnabled === null
                ? 'não confirmado'
                : codexEnabled
                  ? 'autorizado para esta conta'
                  : 'não autorizado'}
              . Este consentimento não inicia a DAILY nem ativa os horários
              07:00/17:00.
            </p>
            {codexEnabled === false && (
              <label>
                <input
                  type="checkbox"
                  checked={codexConfirmed}
                  disabled={codexBusy}
                  onChange={(event) => setCodexConfirmed(event.target.checked)}
                />
                Autorizo somente a inteligência editorial para minha conta.
              </label>
            )}
            <button
              type="button"
              className="secondary"
              disabled={
                codexBusy ||
                codexEnabled === null ||
                (!codexEnabled && !codexConfirmed)
              }
              onClick={() => void changeCodexPermission(!codexEnabled)}
            >
              {codexBusy
                ? 'Salvando consentimento…'
                : codexEnabled
                  ? 'Revogar inteligência do Codex'
                  : 'Autorizar inteligência do Codex'}
            </button>
            {codexMessage && <p role="status">{codexMessage}</p>}
          </div>
        </section>
        <div className="intelligence-status">
          <ShieldCheck size={20} />
          {data?.run ? (
            <span>
              Versão {data.run.analysis_version} · {data.run.observation_count}{' '}
              publicações analisadas · última análise{' '}
              {new Date(data.run.as_of).toLocaleString('pt-BR')}
            </span>
          ) : (
            <span>
              SEM DADOS SUFICIENTES — Conecte contas e colete métricas oficiais.
              Fixtures de teste não aparecem aqui.
            </span>
          )}
        </div>
        <div className="intelligence-filters">
          <label>
            Dimensão{' '}
            <select
              value={dimension}
              onChange={(event) => setDimension(event.target.value)}
            >
              <option value="all">Todas</option>
              {Object.entries(labels).map(([key, label]) => (
                <option key={key} value={key}>
                  {label}
                </option>
              ))}
            </select>
          </label>
          <label>
            Plataforma{' '}
            <select
              value={platform}
              onChange={(event) => setPlatform(event.target.value)}
            >
              <option value="all">Todas</option>
              <option value="instagram">Instagram</option>
              <option value="facebook">Facebook</option>
              <option value="tiktok">TikTok</option>
            </select>
          </label>
          <label>
            Formato{' '}
            <select
              value={format}
              onChange={(event) => setFormat(event.target.value)}
            >
              <option value="all">Todos</option>
              {formats.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
          </label>
          <label>
            Idade da coleta{' '}
            <select
              value={period}
              onChange={(event) => setPeriod(event.target.value)}
            >
              <option value="all">Todas</option>
              {['0-6h', '6-24h', '24-48h', '48h-7d', '7d+'].map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
          </label>
          <label>
            Tema, pilar ou série{' '}
            <input
              value={subjectQuery}
              onChange={(event) => setSubjectQuery(event.target.value)}
              placeholder="Filtrar assunto"
            />
          </label>
        </div>
        <div className="intelligence-grid">
          <section className="panel">
            <p className="eyebrow">COMPARAÇÃO POR COHORT</p>
            <h2>Desempenho</h2>
            {!summaries.length ? (
              <p>SEM DADOS SUFICIENTES para os filtros selecionados.</p>
            ) : (
              <div className="intelligence-list">
                {summaries.map((row) => (
                  <div key={row.dimension + row.subject + row.cohort}>
                    <strong>
                      {labels[row.dimension] ?? row.dimension}: {row.subject}
                    </strong>
                    <small>
                      {row.cohort} · n={row.sampleSize}
                    </small>
                    <span>
                      Mediana de compartilhamento:{' '}
                      {percent(row.medianShareRate)}
                    </span>
                    <span>
                      Visualizações medianas:{' '}
                      {row.medianViews ?? 'Indisponível'}
                    </span>
                  </div>
                ))}
              </div>
            )}
            {!!data?.run?.insufficient.length && (
              <p className="intelligence-footnote">
                {data.run.insufficient.length} grupos sem amostra mínima;
                nenhuma conclusão forte foi gerada para eles.
              </p>
            )}
          </section>
          <section className="panel">
            <p className="eyebrow">EVIDÊNCIA E DECISÃO</p>
            <h2>Insights e oportunidades</h2>
            {!findings.length ? (
              <p>SEM DADOS SUFICIENTES para recomendações.</p>
            ) : (
              <div className="intelligence-list">
                {findings.map((finding) => (
                  <div key={finding.id}>
                    <strong>{finding.title}</strong>
                    <small>
                      {labels[finding.dimension]} · {finding.subject} ·{' '}
                      {finding.kind} · confiança {finding.confidence} · n=
                      {finding.sample_size}
                    </small>
                    <p>{finding.recommendation}</p>
                    <details>
                      <summary>Evidência e método</summary>
                      <pre>{JSON.stringify(finding.evidence, null, 2)}</pre>
                    </details>
                    <small>
                      Estado: {finding.status} · validade:{' '}
                      {new Date(finding.expires_at).toLocaleDateString('pt-BR')}
                    </small>
                    {finding.status === 'PROPOSED' && (
                      <div className="intelligence-actions">
                        <button
                          disabled={busy}
                          onClick={() => void decide(finding.id, true)}
                        >
                          Aprovar
                        </button>
                        <button
                          className="secondary"
                          disabled={busy}
                          onClick={() => void decide(finding.id, false)}
                        >
                          Rejeitar
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </section>
          <section className="panel">
            <p className="eyebrow">HISTÓRICO</p>
            <h2>Publicações e janelas</h2>
            {!data?.observations.length ? (
              <p>Nenhum snapshot oficial disponível.</p>
            ) : (
              <div className="intelligence-list">
                {data.observations.map((row) => (
                  <div key={row.publication_id}>
                    <strong>{row.cohort}</strong>
                    <small>
                      {new Date(row.snapshot_at).toLocaleString('pt-BR')} ·
                      idade {Math.round(row.age_hours)}h
                    </small>
                    <span>
                      Visualizações: {row.metrics.views ?? 'Indisponível'} ·
                      Compartilhamentos: {row.metrics.shares ?? 'Indisponível'}
                    </span>
                    <span>
                      Seguidores por mil views:{' '}
                      {row.rates.followers_per_1000_views ??
                        'Indisponível — sem atribuição direta'}
                    </span>
                    <button
                      className="secondary"
                      onClick={() => void showHistory(row.publication_id)}
                    >
                      Ver evolução 1h–7d
                    </button>
                  </div>
                ))}
              </div>
            )}
            {history && (
              <div className="intelligence-history">
                <h3>
                  Evolução da publicação {history.publicationId.slice(0, 8)}
                </h3>
                {history.rows.map((row) => (
                  <p key={row.hours}>
                    {row.hours}h:{' '}
                    {row.capturedAt
                      ? 'views ' +
                        (row.metrics.views ?? 'indisponível') +
                        ', share rate ' +
                        percent(row.rates.share_rate)
                      : 'Sem coleta nesta janela'}
                  </p>
                ))}
              </div>
            )}
          </section>
          <section className="panel">
            <p className="eyebrow">MEMÓRIA EDITORIAL</p>
            <h2>Aprendizado e limites</h2>
            <p>
              {data?.learnings.length ?? 0} aprendizados aprovados ·{' '}
              {data?.adjustments.length ?? 0} ajustes registrados.
            </p>
            {!!data?.learnings.length && (
              <div className="intelligence-list">
                {data.learnings.map((learning) => (
                  <div key={learning.id}>
                    <strong>{learning.title || 'Aprendizado editorial'}</strong>
                    <small>
                      {learning.status} · válido até{' '}
                      {new Date(learning.expires_at).toLocaleDateString(
                        'pt-BR',
                      )}
                    </small>
                    <span>{learning.recommendation}</span>
                  </div>
                ))}
              </div>
            )}
            {!!data?.adjustments.length && (
              <div className="intelligence-list">
                {data.adjustments.map((adjustment) => (
                  <div key={adjustment.id}>
                    <strong>Ideia {adjustment.target_id.slice(0, 8)}</strong>
                    <small>
                      {adjustment.status} · potencial{' '}
                      {adjustment.before_value ?? '—'} →{' '}
                      {adjustment.after_value ?? '—'}
                    </small>
                    {adjustment.status === 'APPLIED' && (
                      <button
                        className="secondary"
                        disabled={busy}
                        onClick={() => void revert(adjustment.id)}
                      >
                        Reverter ajuste
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
            <p className="intelligence-footnote">
              DNA, fundamento, segurança e diversidade continuam acima do
              desempenho. O ajuste autônomo começa desligado.
            </p>
            {data?.settings && (
              <div className="intelligence-settings">
                <label>
                  Amostra mínima{' '}
                  <input
                    type="number"
                    min={3}
                    max={20}
                    defaultValue={data.settings.minimum_sample}
                    onBlur={(event) => {
                      const value = Number(event.target.value);
                      if (value !== data.settings?.minimum_sample)
                        void updateSettings('minimum_sample', value);
                    }}
                  />
                </label>
                <label>
                  Janela de análise (dias){' '}
                  <input
                    type="number"
                    min={14}
                    max={365}
                    defaultValue={data.settings.lookback_days}
                    onBlur={(event) => {
                      const value = Number(event.target.value);
                      if (value !== data.settings?.lookback_days)
                        void updateSettings('lookback_days', value);
                    }}
                  />
                </label>
                <label>
                  Impulso autônomo no potencial de ideias (0–5){' '}
                  <input
                    type="number"
                    min={0}
                    max={5}
                    defaultValue={data.settings.autonomous_idea_potential_delta}
                    onBlur={(event) => {
                      const value = Number(event.target.value);
                      if (
                        value !== data.settings?.autonomous_idea_potential_delta
                      )
                        void updateSettings(
                          'autonomous_idea_potential_delta',
                          value,
                        );
                    }}
                  />
                </label>
              </div>
            )}
          </section>
        </div>
      </main>
    </div>
  );
}
